import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Illustration, type IllustrationName } from './illustrations';

export interface EmptyStateProps {
  illustration?: IllustrationName;
  title: ReactNode;
  description?: ReactNode;
  /** Primary + secondary actions. */
  actions?: ReactNode;
  /** Extra content under the actions (tips, links). */
  children?: ReactNode;
  /** `page` = big centred, `inline` = compact (inside tables/panels). */
  size?: 'page' | 'inline';
  className?: string;
}

export function EmptyState({
  illustration = 'inbox',
  title,
  description,
  actions,
  children,
  size = 'page',
  className,
}: EmptyStateProps) {
  const page = size === 'page';
  return (
    <div
      className={cn(
        'flex flex-col items-center text-center animate-rise',
        page ? 'px-6 py-14 sm:py-20' : 'px-4 py-10',
        className,
      )}
    >
      <Illustration name={illustration} className={page ? 'mb-6 w-48' : 'mb-4 w-32'} />
      <h3 className={cn('font-display-tight text-ink', page ? 'text-2xl' : 'text-lg')}>{title}</h3>
      {description && (
        <p
          className={cn(
            'mt-2 max-w-[46ch] text-ink-muted',
            page ? 'text-[0.9375rem] leading-relaxed' : 'text-sm',
          )}
        >
          {description}
        </p>
      )}
      {actions && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">{actions}</div>
      )}
      {children && <div className="mt-6">{children}</div>}
    </div>
  );
}
